<script lang="ts" setup>
import { computed } from 'vue';

import type { SuggestionType } from '@apps/main/new-desktop/modules/suggestionTypeSelector';
import {
  InfoIconPosition,
  SuggestionTypeSelector,
  SuggestionTypeSelectorSize,
} from '@apps/main/new-desktop/modules/suggestionTypeSelector';
import {
  useBrowserStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';

import { AppPreferencesTags } from '@libs/ui';

import type {
  ItineraryFerryLoaderSegment,
  ItineraryFerrySegment,
} from '../../../models';
import {
  isAvailableItineraryFerrySegment,
  isItineraryFerryLoaderSegment,
} from '../../../models';

interface FerrySegmentHeaderProps {
  segment: ItineraryFerrySegment | ItineraryFerryLoaderSegment;
  printMode: boolean;
}

const props = defineProps<FerrySegmentHeaderProps>();

interface AvailableAccommodationSegmentEmits {
  (eventName: 'remove-filter', tagId: string): void;
  (eventName: 'show-more-filters'): void;
  (eventName: 'suggestion-type-selected', suggestionType: SuggestionType): void;
}

const emit = defineEmits<AvailableAccommodationSegmentEmits>();
const statsigEventsStore = useStatsigEventsStore();
const browserStore = useBrowserStore();

const showOrderDropdown = computed<boolean>(
  () =>
    (isItineraryFerryLoaderSegment(props.segment) ||
      isAvailableItineraryFerrySegment(props.segment)) &&
    props.segment.suggestionType !== undefined
);
</script>

<template>
  <div class="available-ferry-segment-header">
    <template v-if="!props.printMode">
      <SuggestionTypeSelector
        v-if="
          showOrderDropdown &&
          (isItineraryFerryLoaderSegment(props.segment) ||
            isAvailableItineraryFerrySegment(props.segment)) &&
          props.segment.suggestionType !== undefined
        "
        font-variant="font-light"
        :suggestion-type="props.segment.suggestionType"
        app-select-type="transparent"
        :show-info-icon="InfoIconPosition.None"
        :size="SuggestionTypeSelectorSize.Normal"
        icon-position="right"
        class="suggestion-type-selector"
        @suggestion-type-selected="emit('suggestion-type-selected', $event)"
      />

      <AppPreferencesTags
        v-if="
          (isItineraryFerryLoaderSegment(props.segment) ||
            isAvailableItineraryFerrySegment(props.segment)) &&
          props.segment.suggestionType
        "
        :is-mobile="browserStore.isSmallerThan1200px"
        :tags="props.segment.filtersPills"
        :add-preferences-text="$t('filter.add_filters')"
        :remove-icon-aria-label="$t('a11y.remove_filter')"
        :show-filters-aria-label="$t('a11y.show_filters')"
        should-hide-add-button
        class="preferences-tags"
        @show-more-filters="emit('show-more-filters')"
        @add-filter="
          statsigEventsStore.logTripOverviewFilterButtonClicked();
          emit('show-more-filters');
        "
        @remove-filter="emit('remove-filter', $event)"
      />
    </template>
  </div>
</template>

<style lang="scss" scoped>
.available-ferry-segment-header {
  width: 100%;

  display: grid;
  grid-template-columns: min-content min-content;
  grid-template-rows: auto;
  align-items: center;
  margin-left: var(--spacing-8);

  .suggestion-type-selector {
    grid-column: 2/3;
    grid-row: 1/-1;

    padding-left: var(--spacing-16);
    margin-right: var(--spacing-16);
    align-self: flex-start;
  }

  .preferences-tags {
    grid-column: 3/4;
    grid-row: 1/-1;
    margin-right: var(--spacing-16);
  }

  @include for-less-or-equal-than-599-px {
    display: grid;
    grid-template-columns: min-content 1fr;
    grid-template-rows: auto auto;
    align-items: center;

    .suggestion-type-selector {
      grid-column: 1/2;
      grid-row: 1/2;
      padding-left: var(--spacing-16);
      margin-right: 0;
    }
    .preferences-tags {
      grid-column: 2/3;
      grid-row: 1/2;
      display: flex;
      justify-content: end;
    }
  }
}
</style>
